import dayjs, { Dayjs } from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';
import { INCOME_GOAL } from '../../common/constants';
import { useAppSelector } from '../../common/hooks';
import { GoalTypeProps, IncomeGoalsFormType } from '../../common/types/clients-entity';
import { ConfigurableDrawer, FormField } from '../../components/organisms/GoalDrawer';
import { ComboSelect, Typography } from '../../components/atoms';
import {
    priorityOptionsFlow2,
    getGoalPriorityEnum,
    getPriorityLabel,
    isAspirePriorityTenant,
    isMonthYearDatesEnabled
} from '../../utils';
import { GoalPriorityInfoText } from '../../components/molecules/GoalPriorityInfoText/GoalPriorityInfoText';
import { Box, useTheme } from '@mui/material';
import { ReactComponent as InfoIcon } from '../../assets/icons/info-blue.svg';

export interface IncomeGoalProps {
    // eslint-disable-next-line no-unused-vars
    handleClose: (flag: boolean) => void;
    onSubmit?: () => void;
    loading?: boolean;
    heading?: string;
    subHeading?: string;
    selectedGoal?: boolean | GoalTypeProps;
    goalData?: any;
    open: boolean;
    currency?: string;
}
export const IncomeGoals = ({ handleClose, selectedGoal, open, goalData }: IncomeGoalProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { goeConfig } = useAppSelector((state) => state.global);
    const goalPriorityValue = goeConfig?.['goalPriorityProbabilities'];
    const goalPriorityValueInPercentage =
        goalPriorityValue &&
        Object.fromEntries(
            Object.entries(goalPriorityValue || {}).map(([key, value]) => [key, (value as number) * 100])
        );
    const initialValues: IncomeGoalsFormType = {
        frequency: undefined,
        goalAmount: 0,
        incomeStartDate: null as Dayjs | null,
        targetDate: null as Dayjs | null,
        goalType: 'Income Goal',
        goalId: '',
        goalName: '',
        goalPriority: undefined
    };
    const goalPriorityEnum = getGoalPriorityEnum(goalPriorityValue);
    const priorityOptions = priorityOptionsFlow2(t, goalPriorityEnum);
    const tenant = useAppSelector((state) => state?.auth?.user?.tenant);
    const getPriorityDisplayLabel = (label: string, value: string) => getPriorityLabel({ label, value }, tenant, t);
    const fields: FormField<IncomeGoalsFormType>[] = [
        {
            name: 'goalName',
            label: t('TEXT_GOAL_NAME'),
            type: 'text',
            placeholder: t('TEXT_GOAL_NAME'),
            required: true,
            validation: {
                pattern: /^(?!\s)(?!.*\s$)([A-Za-z0-9\s’'.\-&/+()áéíóúüñÁÉÍÓÚÜÑàâäèêëîïôöùûçÀÂÄÈÊËÎÏÔÖÙÛÇ])*$/i
            }
        },
        {
            name: 'goalPriority',
            label: t('TEXT_GOAL_PRIORITY'),
            type: 'select',
            required: true,
            customRender: ({ field, error, onChange }) => {
                return (
                    <ComboSelect
                        onSelectChange={(val) => {
                            onChange && onChange(val);
                        }}
                        onBlur={field.onBlur}
                        selectedValue={(field.value as string) || ''}
                        name={`income-goal-priority-${field.name}`}
                        placeholder={t('PRIORITY_TEXT_SELECT') + `(${priorityOptions.length})`}
                        options={priorityOptions}
                        error={!!error}
                        listItemStyle={{
                            padding: '0px 14px'
                        }}
                        renderMenuItem={(option, index = 0) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                {`${index + 1} - ${getPriorityDisplayLabel(option.label, option.value)}`}
                            </Box>
                        )}
                        renderValue={(selected) => {
                            const selectedOption = priorityOptions.find((option) => option.value === selected);
                            if (selectedOption) {
                                return (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {`${selectedOption.index + 1} - ${getPriorityDisplayLabel(
                                            selectedOption.label,
                                            selectedOption.value
                                        )}`}
                                    </Box>
                                );
                            }
                            return (
                                <em style={{ color: theme.palette.info[300] }}>
                                    {t('PRIORITY_TEXT_SELECT') + ` (${priorityOptions.length})`}
                                </em>
                            );
                        }}
                    />
                );
            },
            infoTooltip: <GoalPriorityInfoText values={goalPriorityValueInPercentage} renameWishToAspire={isAspirePriorityTenant(tenant)} />
        },
        {
            name: 'goalAmount',
            label: t('INCOME_GOAL_AMOUNT'),
            type: 'currency',
            required: true,
            colspan: 1,
            validation: {
                validate: (value) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }
                    return true;
                }
            },
            infoTooltip: (
                <Trans i18nKey="TEXT_GOAL_TARGET_AMOUNT_INFO_TEXT" components={{ strong: <strong />, br: <br /> }} />
            ),
            HelperTextComponent: () => (
                <Box mt="4px" display="flex" alignItems="center" gap="4px">
                    <InfoIcon />
                    <Typography
                        variant="labels.small"
                        sx={{ color: theme.palette.info[300], textDecoration: 'underline' }}
                        label={t('TEXT_GOAL_TARGET_AMOUNT_HELPER_TEXT')}
                    />
                </Box>
            )
        },
        {
            name: 'frequency',
            label: t('FREQUENCY'),
            type: 'select',
            required: true,
            placeholder: t('FREQUENCY_TEXT_SELECT'),
            colspan: 1,
            options: [
                { label: t('ANNUALLY'), value: 'Annually' },
                { label: t('MONTHLY'), value: 'Monthly' }
            ],
            onFieldChange: (_value, setValue, clearErrors) => {
                // FF off: changing frequency clears the dates so the picker granularity resets.
                if (isMonthYearDatesEnabled()) return;
                setValue('incomeStartDate', null, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
                setValue('targetDate', null, { shouldValidate: false, shouldDirty: false, shouldTouch: false });
                clearErrors?.(['incomeStartDate', 'targetDate']);
            }
        },
        {
            name: 'incomeStartDate',
            label: t('GOAL_SUMMARY_START_DATE_TEXT'),
            type: 'date',
            required: true,
            colspan: 1,
            datePickerStartDate: (watch) =>
                !isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually'
                    ? dayjs().add(1, 'year').startOf('year').format()
                    : dayjs().add(1, 'month').startOf('month').format(),
            validation: {
                validate: (value, formValues) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }

                    const dateValue = dayjs(value);
                    const startDate = dayjs(value, 'MM-DD-YYYY');
                    const validationDate =
                        !isMonthYearDatesEnabled() && formValues?.frequency?.toLowerCase() == 'annually'
                            ? startDate.add(1, 'year').startOf('year')
                            : startDate.add(1, 'month').startOf('month');

                    if (dateValue.isBefore(dayjs(), 'day')) {
                        return t('TEXT_VALID_FUTURE_DATE');
                    }
                    if (formValues?.targetDate) {
                        const targetDate = formValues?.targetDate;
                        const endDate = dayjs(targetDate, 'MM-DD-YYYY');
                        if (dateValue.isBefore(dayjs(), 'day')) {
                            return t('TEXT_VALID_FUTURE_DATE');
                        }
                        if (startDate.isAfter(endDate, 'day')) {
                            return t('TEXT_VALID_END_DATE_ERROR');
                        }
                        if (endDate.isBefore(validationDate, 'day') || startDate.isSame(endDate, 'day')) {
                            return !isMonthYearDatesEnabled() && formValues?.frequency?.toLowerCase() == 'annually'
                                ? t('TEXT_DATE_AT_LEAST_ONE_YEAR')
                                : t('TEXT_DATE_AT_LEAST_ONE_MONTH');
                        }
                    }

                    return dateValue.isValid();
                }
            },
            isMMYYYY: (watch) => {
                return isMonthYearDatesEnabled() || watch?.()?.frequency != 'Annually';
            },
            isYYYY: (watch) => {
                return !isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually';
            },
            isEnabled: (watch) => {
                return !!watch?.()?.frequency;
            },
            infoTooltip: (
                <Trans
                    i18nKey="GOAL_SUMMARY_TARGET_DATE_TEXT_INFO_TEXT"
                    components={{ strong: <strong />, br: <br /> }}
                />
            )
        },
        {
            name: 'targetDate',
            label: t('GOAL_SUMMARY_END_DATE_TEXT'),
            type: 'date',
            required: true,
            colspan: 1,
            dependencyFields: ['incomeStartDate'],
            disableValidation: false, // Disable default validation to handle it manually in the validate function
            datePickerStartDate: (watch) =>
                !isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually'
                    ? dayjs().add(1, 'year').startOf('year').format()
                    : dayjs().add(1, 'month').startOf('month').format(),
            validation: {
                validate: (value, formValues) => {
                    if (!value) {
                        return t('TEXT_THIS_IS_REQUIRED_FIELD');
                    }

                    const today = dayjs();
                    const dateValue = dayjs(value, 'MM-DD-YYYY');
                    if (dateValue.isBefore(today, 'day')) {
                        return t('TEXT_VALID_FUTURE_DATE');
                    }
                    if (formValues?.incomeStartDate && dateValue.isValid()) {
                        const incomeStartDate = formValues?.incomeStartDate;
                        const startDate = dayjs(incomeStartDate, 'MM-DD-YYYY');
                        const validationDate =
                            !isMonthYearDatesEnabled() && formValues?.frequency?.toLowerCase() == 'annually'
                                ? startDate.add(1, 'year').startOf('year')
                                : startDate.add(1, 'month').startOf('month');

                        if (startDate.isAfter(dateValue, 'day')) {
                            return false;
                        }
                        if (dateValue.isBefore(validationDate, 'day') || startDate.isSame(dateValue, 'day')) {
                            return false;
                        }
                    }
                    return dateValue.isValid();
                }
            },
            isMMYYYY: (watch) => {
                return isMonthYearDatesEnabled() || watch?.()?.frequency != 'Annually';
            },
            isYYYY: (watch) => {
                return !isMonthYearDatesEnabled() && watch?.()?.frequency == 'Annually';
            },
            isEnabled: (watch) => {
                return !!watch?.()?.frequency;
            },
            infoTooltip: (
                <Trans
                    i18nKey="GOAL_SUMMARY_TARGET_DATE_TEXT_INFO_TEXT"
                    components={{ strong: <strong />, br: <br /> }}
                />
            )
        }
    ];

    return (
        <ConfigurableDrawer<IncomeGoalsFormType>
            open={open}
            handleClose={handleClose}
            selectedGoal={selectedGoal}
            goalData={goalData}
            goalType={INCOME_GOAL}
            fields={fields}
            defaultValues={initialValues}
            drawerLabel={t('BANORTE_INCOME_GOAL_DRAWER_LABEL')}
        />
    );
};
